import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Baby, Users, DollarSign, TrendingUp, Building2 } from 'lucide-react';
import NavigationControls from '../components/NavigationControls';
import PartieBadge from '../components/PartieBadge';
import { useLanguage } from '../contexts/LanguageContext';
import {
  TAM_GARDERIES_PLACES,
  RATIO_GARDERIE_EXTERNE,
  TAM_GARDERIES_PLACES_EXTERNE,
  ENFANTS_PAR_GARDERIE,
  PRIX_REPAS_GARDERIE,
  JOURS_GARDERIE,
  TAM_GMV_GARDERIES,
  TRAITEURS_EQUIV_GARDERIES,
} from '../mock';
import { formatNumber, formatCurrency, formatCompact } from '../lib/utils';

export default function MarcheGarderiesPage() {
  const { language } = useLanguage();
  const t = {
    fr: {
      titre: 'Marché — Garderies (0-5 ans)',
      intro: "EcoLunch cible le segment des garderies 0-5 ans réglementées. Les enfants 6-12 ans (avant/après école) sont exclus du TAM garderie car ils relèvent du vertical école. La modélisation s'appuie sur les données pancanadiennes ECEC Canada 2024/2025 et Statistique Canada.",
      tamTitre: 'TAM Garderies Canada',
      tamPlaces: 'Places 0-5 réglementées',
      ratioInterne: 'Cuisine interne (42,8%)',
      ratioExterne: 'Service traiteur externe (57,2%)',
      placesAdressables: 'Places adressables (externes)',
      hypothesesTitre: 'Hypothèses opérationnelles',
      enfantsParGarderie: 'Enfants moyens / garderie',
      participation: 'Taux de participation',
      participationVal: '100%',
      prixRepas: 'Prix moyen repas',
      joursAnnee: 'Jours d\'opération / an',
      gmvTitre: 'GMV potentiel annuel',
      gmvAnnuel: 'GMV garderies externes',
      traiteursTitre: 'Traiteurs-équivalents (garderies)',
      traiteursDesc: '15 garderies par traiteur local structuré',
      sourcesTitre: 'Sources',
      sources: [
        'ECEC Canada 2024/2025 — espaces, financement, réglementation, gouvernance',
        'Statistique Canada (2024) — 14 523 fournisseurs en centre, 909 158 enfants',
        '42,8% des centres préparent les repas en cuisine interne (donc exclus du marché traiteur externe)',
      ],
    },
    en: {
      titre: 'Market — Daycares (0-5 years)',
      intro: 'EcoLunch targets the regulated 0-5 daycare segment. Children aged 6-12 (before/after school) are excluded from the daycare TAM as they belong to the school vertical. The modeling is based on ECEC Canada 2024/2025 and Statistics Canada pan-Canadian data.',
      tamTitre: 'Daycare TAM Canada',
      tamPlaces: 'Regulated 0-5 places',
      ratioInterne: 'Internal kitchen (42.8%)',
      ratioExterne: 'External caterer service (57.2%)',
      placesAdressables: 'Addressable places (external)',
      hypothesesTitre: 'Operational assumptions',
      enfantsParGarderie: 'Avg. children / daycare',
      participation: 'Participation rate',
      participationVal: '100%',
      prixRepas: 'Avg. meal price',
      joursAnnee: 'Operating days / year',
      gmvTitre: 'Annual GMV potential',
      gmvAnnuel: 'External daycare GMV',
      traiteursTitre: 'Traiteur-equivalents (daycares)',
      traiteursDesc: '15 daycares per structured local caterer',
      sourcesTitre: 'Sources',
      sources: [
        'ECEC Canada 2024/2025 — spaces, funding, regulation, governance',
        'Statistics Canada (2024) — 14,523 centre providers, 909,158 children',
        '42.8% of centres prepare meals in internal kitchens (therefore excluded from external caterer market)',
      ],
    },
  }[language];

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8">
      <div className="max-w-5xl mx-auto">
        <PartieBadge path="/marche-garderies" />
        <Card className="bg-black/60 border-cyan-500/30 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-2xl sm:text-3xl lg:text-4xl text-white mb-2">{t.titre}</CardTitle>
            <div className="h-1 w-16 sm:w-24 bg-gradient-to-r from-cyan-500 to-lime-500 rounded-full" />
          </CardHeader>
          <CardContent className="space-y-8">
            <p className="text-gray-300 text-base leading-relaxed">{t.intro}</p>

            {/* TAM Garderies */}
            <div>
              <h3 className="text-xl font-bold text-cyan-400 mb-4 flex items-center gap-2">
                <Baby className="h-5 w-5" /> {t.tamTitre}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-gradient-to-br from-cyan-500/15 to-transparent border-cyan-500/30">
                  <CardContent className="p-4">
                    <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">{t.tamPlaces}</p>
                    <p className="text-3xl font-bold text-cyan-400">{formatNumber(TAM_GARDERIES_PLACES)}</p>
                    <p className="text-xs text-gray-500 mt-2">Canada — 0-5 ans</p>
                  </CardContent>
                </Card>
                <Card className="bg-black/40 border-gray-700">
                  <CardContent className="p-4">
                    <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">{t.ratioInterne}</p>
                    <p className="text-3xl font-bold text-gray-500">42,8%</p>
                    <p className="text-xs text-gray-500 mt-2">{language === 'fr' ? 'Repas cuisinés à l\'interne (exclu)' : 'Meals cooked internally (excluded)'}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-lime-500/15 to-transparent border-lime-500/30">
                  <CardContent className="p-4">
                    <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">{t.placesAdressables}</p>
                    <p className="text-3xl font-bold text-lime-400">{formatNumber(TAM_GARDERIES_PLACES_EXTERNE)}</p>
                    <p className="text-xs text-lime-300 mt-2">{Math.round(RATIO_GARDERIE_EXTERNE * 100)}% {language === 'fr' ? 'du marché' : 'of market'}</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Hypothèses opérationnelles */}
            <div>
              <h3 className="text-xl font-bold text-orange-400 mb-4 flex items-center gap-2">
                <Users className="h-5 w-5" /> {t.hypothesesTitre}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: t.enfantsParGarderie, value: ENFANTS_PAR_GARDERIE, color: 'cyan' },
                  { label: t.participation, value: t.participationVal, color: 'lime' },
                  { label: t.prixRepas, value: `${PRIX_REPAS_GARDERIE}$`, color: 'orange' },
                  { label: t.joursAnnee, value: JOURS_GARDERIE, color: 'purple' },
                ].map((item, i) => (
                  <div key={i} className={`p-3 bg-black/40 rounded-lg border border-${item.color}-500/20`}>
                    <p className="text-xs text-gray-400 mb-1">{item.label}</p>
                    <p className={`text-2xl font-bold text-${item.color}-400`}>{item.value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* GMV potentiel */}
            <div className="p-6 bg-gradient-to-br from-lime-500/15 via-cyan-500/10 to-transparent rounded-xl border-2 border-lime-500/40">
              <h3 className="text-xl font-bold text-lime-400 mb-4 flex items-center gap-2">
                <TrendingUp className="h-5 w-5" /> {t.gmvTitre}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">{t.gmvAnnuel}</p>
                  <p className="text-4xl font-bold text-lime-400">{formatCompact(TAM_GMV_GARDERIES)}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    {formatNumber(TAM_GARDERIES_PLACES_EXTERNE)} × {PRIX_REPAS_GARDERIE}$ × {JOURS_GARDERIE} {language === 'fr' ? 'jours' : 'days'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                    <Building2 className="h-3 w-3" /> {t.traiteursTitre}
                  </p>
                  <p className="text-4xl font-bold text-cyan-400">{formatNumber(TRAITEURS_EQUIV_GARDERIES)}</p>
                  <p className="text-xs text-gray-500 mt-2">{t.traiteursDesc}</p>
                </div>
              </div>
            </div>

            {/* Sources */}
            <div className="p-4 bg-black/40 rounded-lg border border-gray-700">
              <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">{t.sourcesTitre}</h4>
              <ul className="space-y-2">
                {t.sources.map((src, i) => (
                  <li key={i} className="text-xs text-gray-400 flex items-start gap-2">
                    <span className="text-cyan-500 mt-0.5">•</span>
                    <span>{src}</span>
                  </li>
                ))}
              </ul>
            </div>

            <NavigationControls />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
